/**
 * Notification System v2 in the Admin Portal: one client per signed-in session,
 * wired to this app's API client, router and sign-out. The shared pieces
 * (notifyCore.js, notifyUi.jsx, notify.css, public/notify-sw.js) are the same
 * files in care-provider and care.
 */
import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import request from '../../assets/utils/http-request';
import config from '../../assets/utils/config';
import { createNotifyClient } from './notifyCore';
import { NotifyProvider, NotificationCentre, NotificationPermissionPrompt, NotificationToasts, useNotify } from './notifyUi';

export { NotificationBell, NotificationPreferences, useNotify } from './notifyUi';

const WS_URL = process.env.REACT_APP_NOTIFY_WS_URL || 'wss://ws.amanhmo.com/v2';

let client = null;
let clientToken = null;
let tokenOverride = null;

function readToken() {
  try {
    const user = JSON.parse(localStorage.getItem(config.key.user) || 'null');
    return user?.jwt || null;
  } catch (e) {
    return null;
  }
}

export async function apiRequest(method, path, body) {
  const token = tokenOverride || readToken();
  const options = { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  const url = `/${path}`;
  try {
    let resp;
    if (method === 'get') resp = await request.get(url, { ...options, params: body });
    else if (method === 'delete') resp = await request.delete(url, { ...options, data: body });
    else resp = await request[method](url, body, options);
    return resp?.data;
  } catch (e) {
    return e?.response?.data || { error: 1, message: e?.message };
  }
}

/** The session's client, created on first use and replaced when the signed-in user changes. */
export function getNotifyClient() {
  const token = readToken();
  if (!token) return null;
  if (!client || clientToken !== token) {
    if (client) client.stop();
    client = createNotifyClient({ portal: 'admin', appCode: 2, wsUrl: WS_URL, getToken: readToken, request: apiRequest });
    clientToken = token;
  }
  return client;
}

/** Call before the session is cleared: stops the live connection and removes this browser's push subscription. */
export function signOutNotify() {
  if (!client) return;
  const c = client;
  tokenOverride = readToken();
  client = null;
  clientToken = null;
  c.signOut().finally(() => { tokenOverride = null; });
}

export function AdminNotifyProvider({ children }) {
  const navigate = useNavigate();
  const token = readToken();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const c = useMemo(() => getNotifyClient(), [token]);
  return (
    <NotifyProvider client={c} navigate={navigate}>
      {children}
      {c ? <NotificationToasts /> : null}
      {c ? <NotificationPermissionPrompt /> : null}
    </NotifyProvider>
  );
}

/** /notifications: the v2 centre once the framework is on, otherwise the previous page. */
export function NotificationsRoute({ legacy }) {
  const ctx = useNotify();
  const location = useLocation();
  if (ctx && !ctx.state?.ready) return null;
  if (ctx && ctx.state?.enabled) {
    return <NotificationCentre location={location} />;
  }
  return legacy;
}
